import { Link } from "react-router-dom"
import { motion } from "framer-motion"
import {
  Users,
  PenToolIcon as Tool,
  Calendar,
  Wallet,
  Bell,
  Smartphone,
  ArrowRight,
  CheckCircle2,
  Apple,
  Play,
  Sparkles,
  ScanSearch,
  Eye,
  MousePointerClick,
  CircleCheckBig,
  AlertTriangle,
  AlertCircle,
  Info,
} from "lucide-react"
import FeatureCard from "../components/FeatureCard"
import SignupForm from "../components/SignupForm"

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
}

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
}

export default function HomePage() {
  const features = [
    {
      title: "Member Management",
      description:
        "Send email and real-time notifications, upload and store documents and important information.",
      icon: "users",
    },
    {
      title: "Usage & Maintenance Tracking",
      description:
        "Track usage and maintenance, upload photos, get reminders for due dates.",
      icon: "tool",
    },
    {
      title: "Reservations & Scheduling",
      description:
        "Manage reservations and scheduling, synchronize with Google calendar.",
      icon: "calendar",
    },
    {
      title: "Financial Management",
      description:
        "Handle financial aspects, transactions, dues, settlements, and more.",
      icon: "wallet",
    },
    {
      title: "Notifications & Reminders",
      description:
        "Get notifications and reminders for due dates, payments, and other important events.",
      icon: "bell",
    },
    {
      title: "Mobile App",
      description:
        "Manage your partnerships on the go with our native iOS and Android apps.",
      icon: "smartphone",
    },
  ]

  const mobileFeatures = [
    "Dashboard with partnership stats and quick actions",
    "Schedule and manage reservations from anywhere",
    "Track maintenance requests with photo uploads",
    "View finances, dues, and transactions in real-time",
    "Receive push notifications for important updates",
    "Full logbook and usage tracking",
  ]

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#0a0f1a]">
      {/* Gradient orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full bg-teal-500/10 blur-[120px]" />
        <div className="absolute top-1/3 -left-40 h-[500px] w-[500px] rounded-full bg-emerald-500/8 blur-[100px]" />
        <div className="absolute bottom-0 right-1/4 h-[400px] w-[400px] rounded-full bg-teal-400/6 blur-[100px]" />
      </div>

      {/* Subtle grid pattern */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#0a0f1a]/80 backdrop-blur-xl">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="PartnerEZ" className="h-8 w-auto brightness-0 invert drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]" />
            </Link>
            <div className="hidden items-center gap-8 md:flex">
              <a
                href="#features"
                className="text-sm text-gray-400 transition-colors hover:text-white"
              >
                Features
              </a>
              <a
                href="#mobile"
                className="text-sm text-gray-400 transition-colors hover:text-white"
              >
                Mobile App
              </a>
              <a
                href="#ai-review"
                className="text-sm text-gray-400 transition-colors hover:text-white"
              >
                AI Review
              </a>
              <a
                href="#how-it-works"
                className="text-sm text-gray-400 transition-colors hover:text-white"
              >
                How It Works
              </a>
              <a
                href="#signup"
                className="text-sm text-gray-400 transition-colors hover:text-white"
              >
                Join Beta
              </a>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="https://app.partnerez.com"
                className="rounded-full border border-white/10 px-5 py-2 text-sm font-medium text-white transition-all hover:border-white/20 hover:bg-white/5"
              >
                Login
              </Link>
              <a
                href="#signup"
                className="rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 px-5 py-2 text-sm font-medium text-white shadow-lg shadow-teal-500/20 transition-all hover:shadow-teal-500/30"
              >
                Get Started
              </a>
            </div>
          </div>
        </div>
      </nav>

      <div className="relative z-10">
        {/* Hero Section */}
        <section className="container mx-auto px-4 pt-32 pb-20 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="mx-auto max-w-4xl text-center"
          >
            <motion.div variants={fadeIn} custom={0}>
              <span className="inline-flex items-center gap-2 rounded-full border border-teal-500/20 bg-teal-500/10 px-4 py-1.5 text-sm text-teal-400">
                <span className="h-1.5 w-1.5 rounded-full bg-teal-400" />
                Now available on iOS & Android
              </span>
            </motion.div>

            <motion.h1
              variants={fadeIn}
              custom={1}
              className="mt-8 text-5xl font-bold tracking-tight text-white sm:text-6xl lg:text-7xl"
            >
              Partnerships{" "}
              <span className="bg-gradient-to-r from-teal-400 via-emerald-400 to-teal-300 bg-clip-text text-transparent">
                Made Easy
              </span>
            </motion.h1>

            <motion.p
              variants={fadeIn}
              custom={2}
              className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-gray-400"
            >
              Simplify shared ownership of planes, boats, RVs, homes and more
              with our all-in-one partnership management platform. Available on
              web, iOS, and Android.
            </motion.p>

            <motion.div
              variants={fadeIn}
              custom={3}
              className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
            >
              <a
                href="#signup"
                className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 px-8 py-3.5 text-base font-medium text-white shadow-lg shadow-teal-500/25 transition-all hover:shadow-teal-500/40"
              >
                Join Beta Waitlist
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
              <Link
                to="https://app.partnerez.com"
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-8 py-3.5 text-base font-medium text-white backdrop-blur-sm transition-all hover:border-white/20 hover:bg-white/10"
              >
                Open Web App
              </Link>
            </motion.div>

            {/* App store badges */}
            <motion.div
              variants={fadeIn}
              custom={4}
              className="mt-8 flex items-center justify-center gap-4"
            >
              <a
                href="https://apps.apple.com/app/partnerez"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-5 py-2.5 text-sm text-white transition-all hover:border-white/20 hover:bg-white/10"
              >
                <Apple className="h-5 w-5" />
                <div className="text-left">
                  <div className="text-[10px] leading-none text-gray-400">
                    Download on the
                  </div>
                  <div className="font-semibold leading-tight">App Store</div>
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.partnerez.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-5 py-2.5 text-sm text-white transition-all hover:border-white/20 hover:bg-white/10"
              >
                <Play className="h-5 w-5" />
                <div className="text-left">
                  <div className="text-[10px] leading-none text-gray-400">
                    Get it on
                  </div>
                  <div className="font-semibold leading-tight">Google Play</div>
                </div>
              </a>
            </motion.div>
          </motion.div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={stagger}
              className="mx-auto max-w-7xl"
            >
              <motion.div variants={fadeIn} custom={0} className="text-center">
                <h2 className="text-3xl font-bold text-white sm:text-4xl">
                  Everything you need to manage{" "}
                  <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
                    shared assets
                  </span>
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-400">
                  PartnerEZ simplifies every aspect of ownership and partnership
                  management
                </p>
              </motion.div>

              <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {features.map((feature, index) => (
                  <FeatureCard
                    key={index}
                    title={feature.title}
                    description={feature.description}
                    icon={feature.icon}
                    delay={index * 0.1}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Mobile App Section */}
        <section id="mobile" className="py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={stagger}
              className="mx-auto max-w-7xl"
            >
              <div className="grid items-center gap-16 lg:grid-cols-2">
                {/* Left: Info */}
                <div>
                  <motion.div variants={fadeIn} custom={0}>
                    <span className="inline-flex items-center gap-2 rounded-full border border-teal-500/20 bg-teal-500/10 px-4 py-1.5 text-sm text-teal-400">
                      <Smartphone className="h-4 w-4" />
                      Native Mobile Apps
                    </span>
                  </motion.div>

                  <motion.h2
                    variants={fadeIn}
                    custom={1}
                    className="mt-6 text-3xl font-bold text-white sm:text-4xl"
                  >
                    Manage your partnerships{" "}
                    <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
                      on the go
                    </span>
                  </motion.h2>

                  <motion.p
                    variants={fadeIn}
                    custom={2}
                    className="mt-4 text-lg text-gray-400"
                  >
                    Our Flutter-powered mobile app brings the full PartnerEZ
                    experience to your pocket. Built with Material 3 design, it
                    offers a seamless experience on both iOS and Android.
                  </motion.p>

                  <motion.ul
                    variants={fadeIn}
                    custom={3}
                    className="mt-8 space-y-4"
                  >
                    {mobileFeatures.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-teal-400" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </motion.ul>

                  <motion.div
                    variants={fadeIn}
                    custom={4}
                    className="mt-10 flex flex-wrap gap-4"
                  >
                    <a
                      href="https://apps.apple.com/app/partnerez"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 rounded-xl bg-white px-6 py-3 text-gray-900 shadow-lg transition-all hover:shadow-xl"
                    >
                      <Apple className="h-7 w-7" />
                      <div>
                        <div className="text-xs leading-none text-gray-500">
                          Download on the
                        </div>
                        <div className="text-lg font-semibold leading-tight">
                          App Store
                        </div>
                      </div>
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.partnerez.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 rounded-xl bg-white px-6 py-3 text-gray-900 shadow-lg transition-all hover:shadow-xl"
                    >
                      <Play className="h-7 w-7" />
                      <div>
                        <div className="text-xs leading-none text-gray-500">
                          Get it on
                        </div>
                        <div className="text-lg font-semibold leading-tight">
                          Google Play
                        </div>
                      </div>
                    </a>
                  </motion.div>
                </div>

                {/* Right: Phone mockup */}
                <motion.div
                  variants={fadeIn}
                  custom={2}
                  className="flex justify-center"
                >
                  <div className="relative">
                    {/* Glow behind phone */}
                    <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-br from-teal-500/20 to-emerald-500/20 blur-3xl" />

                    {/* Phone frame */}
                    <div className="relative rounded-[2.5rem] border border-white/10 bg-gradient-to-b from-gray-800 to-gray-900 p-3 shadow-2xl">
                      <div className="overflow-hidden rounded-[2rem] bg-[#0f172a]">
                        {/* Status bar */}
                        <div className="flex items-center justify-between px-6 py-2">
                          <span className="text-xs font-medium text-white">
                            9:41
                          </span>
                          <div className="flex gap-1">
                            <div className="h-2.5 w-4 rounded-sm bg-white/60" />
                            <div className="h-2.5 w-2.5 rounded-full bg-white/60" />
                            <div className="h-2.5 w-6 rounded-sm bg-white/60" />
                          </div>
                        </div>

                        {/* App content mock */}
                        <div className="w-[280px] px-4 pb-4">
                          {/* Header */}
                          <div className="mb-4 flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-400">
                                Good morning
                              </p>
                              <p className="text-base font-semibold text-white">
                                Dashboard
                              </p>
                            </div>
                            <div className="h-8 w-8 rounded-full bg-teal-500/20" />
                          </div>

                          {/* Stat cards */}
                          <div className="mb-3 grid grid-cols-2 gap-2">
                            {[
                              { label: "My Dues", value: "$450" },
                              { label: "Reservations", value: "3" },
                              { label: "Maintenance", value: "1" },
                              { label: "Assets", value: "2" },
                            ].map((stat) => (
                              <div
                                key={stat.label}
                                className="rounded-lg bg-white/5 p-2.5"
                              >
                                <p className="text-[10px] text-gray-400">
                                  {stat.label}
                                </p>
                                <p className="text-sm font-bold text-white">
                                  {stat.value}
                                </p>
                              </div>
                            ))}
                          </div>

                          {/* Quick actions */}
                          <div className="mb-3 flex gap-2">
                            {["Log", "Reserve", "Report", "Pay"].map(
                              (action) => (
                                <div
                                  key={action}
                                  className="flex-1 rounded-lg bg-teal-500/10 py-2 text-center"
                                >
                                  <p className="text-[9px] font-medium text-teal-400">
                                    {action}
                                  </p>
                                </div>
                              )
                            )}
                          </div>

                          {/* Activity feed */}
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-gray-400">
                              Recent Activity
                            </p>
                            {[1, 2, 3].map((i) => (
                              <div
                                key={i}
                                className="flex items-center gap-2 rounded-lg bg-white/5 p-2"
                              >
                                <div className="h-6 w-6 rounded-full bg-teal-500/20" />
                                <div className="flex-1">
                                  <div className="h-2 w-3/4 rounded bg-white/10" />
                                  <div className="mt-1 h-1.5 w-1/2 rounded bg-white/5" />
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Bottom nav */}
                          <div className="mt-3 flex items-center justify-around rounded-xl bg-white/5 py-2">
                            {["Home", "Schedule", "Maint.", "Finance", "More"].map(
                              (tab, i) => (
                                <div key={tab} className="text-center">
                                  <div
                                    className={`mx-auto h-4 w-4 rounded ${i === 0 ? "bg-teal-400" : "bg-white/10"}`}
                                  />
                                  <p
                                    className={`mt-0.5 text-[8px] ${i === 0 ? "text-teal-400" : "text-gray-500"}`}
                                  >
                                    {tab}
                                  </p>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* AI Logbook Review Section */}
        <section id="ai-review" className="py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={stagger}
              className="mx-auto max-w-7xl"
            >
              <motion.div variants={fadeIn} custom={0} className="text-center">
                <span className="inline-flex items-center gap-2 rounded-full border border-purple-500/20 bg-purple-500/10 px-4 py-1.5 text-sm text-purple-400">
                  <Sparkles className="h-4 w-4" />
                  AI-Powered
                </span>
                <h2 className="mt-6 text-3xl font-bold text-white sm:text-4xl">
                  Smart Logbook{" "}
                  <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                    Review & Fix
                  </span>
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-400">
                  Our AI scans your logbook entries, catches discrepancies, and
                  lets you preview and apply fixes — all before committing any
                  changes.
                </p>
              </motion.div>

              {/* Flow steps */}
              <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                {[
                  {
                    icon: ScanSearch,
                    step: "Scan",
                    title: "Tap the wand",
                    description:
                      "Tap the wand icon in the app bar. Gemini AI analyzes all your logbook entries for issues.",
                    color: "purple",
                  },
                  {
                    icon: Eye,
                    step: "Preview",
                    title: "Review findings",
                    description:
                      "A results sheet shows each issue with severity badges, descriptions, and before → after diffs.",
                    color: "pink",
                  },
                  {
                    icon: MousePointerClick,
                    step: "Apply",
                    title: "Choose & apply",
                    description:
                      'Select which fixes to include with checkboxes, then tap "Apply N Fixes" to write changes.',
                    color: "violet",
                  },
                  {
                    icon: CircleCheckBig,
                    step: "Confirm",
                    title: "Done",
                    description:
                      "A green checkmark confirms fixes applied. Your logbook list refreshes automatically.",
                    color: "emerald",
                  },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    variants={fadeIn}
                    custom={index + 1}
                    className="group relative rounded-2xl border border-white/5 bg-white/[0.02] p-6 backdrop-blur-sm transition-all hover:border-purple-500/20 hover:bg-white/[0.04]"
                  >
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg shadow-purple-500/20">
                      <item.icon className="h-6 w-6 text-white" />
                    </div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-purple-400">
                      {item.step}
                    </p>
                    <h3 className="mb-2 text-lg font-semibold text-white">
                      {item.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-gray-400">
                      {item.description}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Preview mockup card */}
              <motion.div
                variants={fadeIn}
                custom={5}
                className="mx-auto mt-12 max-w-2xl"
              >
                <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm">
                  {/* Header bar */}
                  <div className="flex items-center justify-between border-b border-white/5 px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Sparkles className="h-5 w-5 text-purple-400" />
                      <span className="font-semibold text-white">
                        AI Review Results
                      </span>
                    </div>
                    <span className="rounded-full bg-purple-500/10 px-3 py-1 text-xs font-medium text-purple-400">
                      3 Fixable
                    </span>
                  </div>

                  {/* Issue items */}
                  <div className="divide-y divide-white/5">
                    {[
                      {
                        severity: "critical",
                        icon: AlertTriangle,
                        color: "red",
                        bgColor: "bg-red-500/10",
                        textColor: "text-red-400",
                        title: "Hobbs start doesn't match previous end",
                        field: "hobbsStart",
                        before: "1234.5",
                        after: "1235.0",
                      },
                      {
                        severity: "warning",
                        icon: AlertCircle,
                        color: "amber",
                        bgColor: "bg-amber-500/10",
                        textColor: "text-amber-400",
                        title: "Flight duration exceeds typical range",
                        field: "duration",
                        before: "12.5 hrs",
                        after: "2.5 hrs",
                      },
                      {
                        severity: "info",
                        icon: Info,
                        color: "blue",
                        bgColor: "bg-blue-500/10",
                        textColor: "text-blue-400",
                        title: "Missing fuel quantity on return",
                        field: "fuelEnd",
                        before: "—",
                        after: "42 gal",
                      },
                    ].map((issue, i) => (
                      <div key={i} className="flex items-start gap-4 px-6 py-4">
                        <input
                          type="checkbox"
                          checked
                          readOnly
                          className="mt-1 h-4 w-4 rounded border-white/20 bg-white/5 text-purple-500 accent-purple-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-flex items-center gap-1 rounded-full ${issue.bgColor} px-2 py-0.5 text-[10px] font-semibold uppercase ${issue.textColor}`}
                            >
                              <issue.icon className="h-3 w-3" />
                              {issue.severity}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-300">
                            {issue.title}
                          </p>
                          <div className="mt-2 flex items-center gap-2 text-xs">
                            <code className="rounded bg-red-500/10 px-2 py-0.5 text-red-400">
                              {issue.field}: {issue.before}
                            </code>
                            <ArrowRight className="h-3 w-3 text-gray-500" />
                            <code className="rounded bg-emerald-500/10 px-2 py-0.5 text-emerald-400">
                              {issue.field}: {issue.after}
                            </code>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Apply bar */}
                  <div className="border-t border-white/5 bg-white/[0.02] px-6 py-4">
                    <div className="flex items-center justify-end">
                      <div className="rounded-full bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-2 text-sm font-medium text-white shadow-lg shadow-purple-500/20">
                        Apply 3 Fixes
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={stagger}
              className="mx-auto max-w-7xl"
            >
              <motion.div variants={fadeIn} custom={0} className="text-center">
                <h2 className="text-3xl font-bold text-white sm:text-4xl">
                  How PartnerEZ Works
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-400">
                  Get started in minutes and enjoy stress-free partnership
                  management
                </p>
              </motion.div>

              <div className="relative mt-16">
                {/* Connecting line */}
                <div className="absolute top-12 left-0 right-0 hidden h-px bg-gradient-to-r from-transparent via-teal-500/30 to-transparent md:block" />

                <div className="grid gap-8 md:grid-cols-3">
                  {[
                    {
                      step: "01",
                      title: "Create Your Partnership",
                      description:
                        "Set up your partnership in minutes, invite members, and define ownership shares.",
                    },
                    {
                      step: "02",
                      title: "Manage Together",
                      description:
                        "Collaborate on scheduling, maintenance, and financial decisions in one place.",
                    },
                    {
                      step: "03",
                      title: "Enjoy Worry-Free",
                      description:
                        "Focus on enjoying your shared asset while PartnerEZ handles the complexities.",
                    },
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      variants={fadeIn}
                      custom={index + 1}
                      className="group relative rounded-2xl border border-white/5 bg-white/[0.02] p-8 backdrop-blur-sm transition-all hover:border-teal-500/20 hover:bg-white/[0.04]"
                    >
                      <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 text-lg font-bold text-white shadow-lg shadow-teal-500/20">
                        {item.step}
                      </div>
                      <h3 className="mb-3 text-xl font-semibold text-white">
                        {item.title}
                      </h3>
                      <p className="text-gray-400 leading-relaxed">
                        {item.description}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Signup Section */}
        <section id="signup" className="py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={stagger}
              className="mx-auto max-w-3xl"
            >
              <div className="relative overflow-hidden rounded-3xl border border-white/5 bg-white/[0.02] p-8 backdrop-blur-sm sm:p-12">
                {/* Background glow */}
                <div className="pointer-events-none absolute -top-20 -right-20 h-60 w-60 rounded-full bg-teal-500/10 blur-[80px]" />
                <div className="pointer-events-none absolute -bottom-20 -left-20 h-60 w-60 rounded-full bg-emerald-500/10 blur-[80px]" />

                <div className="relative">
                  <motion.div
                    variants={fadeIn}
                    custom={0}
                    className="text-center"
                  >
                    <h2 className="text-3xl font-bold text-white sm:text-4xl">
                      Join the Beta Waitlist
                    </h2>
                    <p className="mx-auto mt-4 max-w-xl text-lg text-gray-400">
                      Be among the first to experience PartnerEZ and simplify
                      your partnership management.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} custom={1} className="mt-10">
                    <SignupForm />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/5 py-12">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-7xl">
              <div className="flex flex-col items-center justify-between gap-8 md:flex-row">
                <div className="flex items-center gap-2">
                  <img
                    src="/logo.png"
                    alt="PartnerEZ"
                    className="h-6 w-auto brightness-0 invert drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]"
                  />
                </div>
                <div className="flex flex-wrap items-center justify-center gap-8">
                  <a
                    href="https://apps.apple.com/app/partnerez"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    iOS App
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.partnerez.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    Android App
                  </a>
                  <Link
                    to="#"
                    className="text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    to="#"
                    className="text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    Terms of Service
                  </Link>
                  <Link
                    to="mailto:hello@partnerez.com?subject=PartnerEZ%20-%20Beta%20Waitlist"
                    className="text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
              <div className="mt-8 text-center text-sm text-gray-500">
                <p>
                  &copy; {new Date().getFullYear()} PartnerEZ. All rights
                  reserved.
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </main>
  )
}
